import React, { useState, useEffect, useRef, useCallback } from 'react';
import _ from 'lodash';
import { styled } from '@this/constants/themes';
import { reportError } from '@this/lib/bugsnag';
import useClickOutside from '@this/src/lib/hooks/useClickOutside';
import { getFirstFromArray } from '@this/src/util';
import type Department from '@this/domain/department/department2';
import User from '@this/domain/user/user2';
import type TravelerInformation from '@this/domain/traveler_information';

import DepartmentsList from './departments_list/departments_list';
import {
  WorkflowSelectorArrow,
  workflowSelectorTriggerCss
} from '@this/components/shared/workflow_selector_appearance';

type Props = {
  departments: Department[];
  user?: User | TravelerInformation;
  onSelect: (department: Department) => void;
  disabledAddress: boolean;
  allowBlank?: true;
  isRequired: boolean;
  workflowFormWidth: string;
  defaultDepartment?: Department | null;
};

const SelectDepartments: React.FC<Props> = ({
  departments,
  user,
  onSelect,
  disabledAddress,
  allowBlank,
  isRequired,
  workflowFormWidth,
  defaultDepartment
}) => {
  const [showDepartmentsList, setShowDepartmentsList] = useState(false);
  const [selected, setSelected] = useState<Department | undefined>(undefined);
  const selectAreaRef = useRef<HTMLDivElement>(null);

  const findDefaultDepartment = useCallback(() => {
    try {
      const userDepartment = user instanceof User ? user.department : user?.user?.department;
      if (!userDepartment) return undefined;
      return _.find(departments, d => d.id === userDepartment.id);
    } catch (e) {
      reportError(e);
      return undefined;
    }
  }, [departments, user]);

  const handleDepartmentSelect = useCallback(
    (department: Department) => {
      setShowDepartmentsList(false);
      setSelected(department);
      onSelect(department);
    },
    [onSelect]
  );

  const toggleDepartmentsList = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    setShowDepartmentsList(prevState => !prevState);
  }, []);

  const getDefaultDepartment = () => {
    if (departments.length === 0) {
      return undefined;
    }
    if (defaultDepartment) {
      return defaultDepartment;
    }

    const userDepartment = findDefaultDepartment();
    if (userDepartment) {
      return userDepartment;
    }

    if (!allowBlank) {
      return getFirstFromArray(departments, 'SelectDepartments.getDefaultDepartment');
    }

    return undefined;
  };

  // TODO 空配列EffectはReact18でなくしたい
  useEffect(() => {
    const defaultDept = getDefaultDepartment();
    if (defaultDept) {
      setSelected(defaultDept);
      onSelect(defaultDept);
    }
  }, []);

  useClickOutside(
    selectAreaRef,
    () => {
      setShowDepartmentsList(false);
    },
    showDepartmentsList
  );

  try {
    return (
      <SelectDepartmentsWrapper workflowFormWidth={workflowFormWidth} ref={selectAreaRef}>
        <SelectDepartmentsListItem data-testid="fs_open_box" onClick={toggleDepartmentsList}>
          {selected ? (
            <SelectDepartmentsItemSelected data-testid="reserve-confirm-input-shinkansen-department-select-selected">
              <p>{disabledAddress ? selected.description : selected.descriptionWithCode}</p>
            </SelectDepartmentsItemSelected>
          ) : (
            <SelectDepartmentsItemMessage>
              {isRequired ? '(必須)' : '(任意)'}選択してください
            </SelectDepartmentsItemMessage>
          )}
          <WorkflowSelectorArrow open={showDepartmentsList} />
        </SelectDepartmentsListItem>
        {showDepartmentsList && (
          <DepartmentsList
            departments={departments}
            onSelect={handleDepartmentSelect}
            disabledAddress={disabledAddress}
            workflowFormWidth={workflowFormWidth}
          />
        )}
      </SelectDepartmentsWrapper>
    );
  } catch (e) {
    reportError(e);
    return null;
  }
};

const SelectDepartmentsWrapper = styled.div<{ workflowFormWidth?: string }>`
  width: ${props => props.workflowFormWidth || '100%'};
  font-size: 13px;
  margin-bottom: 5px;
  line-height: normal;
`;

const SelectDepartmentsListItem = styled.div`
  ${workflowSelectorTriggerCss};
  width: 100%;
  white-space: normal;
  word-break: break-all;
`;

const SelectDepartmentsItemSelected = styled.div`
  display: flex;
  flex: 1;
  min-width: 0;
  flex-wrap: wrap;
`;

const SelectDepartmentsItemMessage = styled.p`
  color: $black-color;
  flex: 1;
  min-width: 0;
`;

export default SelectDepartments;
